import React, { useEffect, useState, useMemo, useRef } from 'react';
import { ArrowLeft, ExternalLink, Pen, Send, History } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  DatePicker,
  DropdownField,
  Label,
  Textarea,
} from '@roger/ui-components';
import {
  approveTask,
  breakTaskSignatures,
  getPatientCommunication,
  getPatientCommunicationPdf,
  getTaskSignatures,
  signTask,
  updatePatientCommunication,
} from '../../services/api';
import { listAgencyClinicPhysiciansAgencyClinicsAgencyClinicIdPhysiciansGet as listClinicPhysicians } from '../../services/api/sdk.gen';
import type {
  ClinicPhysicianResponse,
  PatientCommunicationResponse,
  TaskSignatureResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { usePdfTab } from '../../hooks/usePdfTab';
import { useToast } from '../../hooks/useToastContext';
import { useAutoSave, SaveDeclinedError } from '../../hooks/useAutoSave';
import { useTaskPermissions } from '../task/TaskDetailPanel';
import {
  TaskHeaderControls,
  TaskWorkflowStatusBadge,
} from '../task/TaskHeaderControls';
import { AttestationDialog } from '../signing/AttestationDialog';
import type { ESignatureCredentialInput } from '../signing/esignatureCredentialApi';
import { SignatureHistoryDialog } from '../signing/SignatureHistoryDialog';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { formatPhysicianName } from '../../utils/formatting';
import { AutoSavePill } from '../AutoSavePill';

interface CommunicationDetailPanelProps {
  communicationId: string;
  onBack?: () => void;
  onUpdated?: () => void;
}

type CommunicationDetailDraft = {
  communication_date: string | null;
  clinic_physician_id: string;
  notes: string;
};

const emptyCommunicationDetailDraft: CommunicationDetailDraft = {
  communication_date: null,
  clinic_physician_id: '',
  notes: '',
};

const communicationDetailDraftFromResponse = (
  comm: PatientCommunicationResponse
): CommunicationDetailDraft => ({
  communication_date: comm.communication_date,
  clinic_physician_id: comm.clinic_physician_id ?? '',
  notes: comm.notes ?? '',
});

export const CommunicationDetailPanel: React.FC<
  CommunicationDetailPanelProps
> = ({ communicationId, onBack, onUpdated }) => {
  const { error: showError } = useToast();
  const user = useStore(state => state.user);
  const openPdfTab = usePdfTab();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  const [communication, setCommunication] =
    useState<PatientCommunicationResponse | null>(null);
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);

  const [signingLoading, setSigningLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfInFlightRef = useRef(false);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyDialogFrameRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (historyDialogFrameRef.current !== null) {
        cancelAnimationFrame(historyDialogFrameRef.current);
      }
    };
  }, []);
  const [savedDraft, setSavedDraft] = useState<CommunicationDetailDraft>(
    emptyCommunicationDetailDraft
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [commRes, sigsRes] = await Promise.all([
        getPatientCommunication({
          path: { communication_id: communicationId },
        }),
        getTaskSignatures({ path: { task_id: communicationId } }),
      ]);
      if (commRes.data) {
        setCommunication(commRes.data);
        setSavedDraft(communicationDetailDraftFromResponse(commRes.data));
      }
      if (sigsRes.data) {
        setSignatures(sigsRes.data);
      }
    } catch (err) {
      console.error('Error fetching communication:', err);
      showError('Load Failed', 'Failed to load communication details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, [communicationId]);

  useEffect(() => {
    if (!communication?.agency_clinic_id) return;
    const clinicId = communication.agency_clinic_id;
    let cancelled = false;
    (async () => {
      try {
        const res = await listClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled && res.data) {
          setPhysicians(res.data as ClinicPhysicianResponse[]);
        }
      } catch (err) {
        console.error('Failed to load physicians:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communication?.agency_clinic_id]);

  const isReadonly = !EDITABLE_STATUSES.has(
    communication?.status ?? 'not_started'
  );

  // Role-based action gating, shared with Visit/Order panels so a communication
  // signs, approves and unlocks exactly like every other task (ROG-2553).
  const { canSign, canApprove, canBreakSignatures } = useTaskPermissions(
    user,
    communication ?? undefined
  );

  const communicationAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('patient_communication')
    );
  }, [attestations]);

  const activeSignatures = useMemo(
    () => signatures.filter(sig => !sig.invalidated_at),
    [signatures]
  );

  const handleAutoSave = React.useCallback(
    async (draft: CommunicationDetailDraft) => {
      if (
        !communication ||
        !draft.communication_date ||
        !draft.notes.trim() ||
        !draft.clinic_physician_id
      ) {
        // Required fields missing — refuse the save by throwing rather than
        // returning, so useAutoSave doesn't record the incomplete draft as the
        // saved baseline (which would make hasUnsavedChanges lie and drop the
        // next real save). The readiness guard still keeps the pill quiet until
        // the form is complete; surfacing "Not saved" on this sticky pill is a
        // separate UX call left to the comms-pill follow-up.
        throw new SaveDeclinedError();
      }

      try {
        const res = await updatePatientCommunication({
          path: { communication_id: communicationId },
          body: {
            communication_date: draft.communication_date,
            communication_type: communication.communication_type,
            assigned_clinician_id: communication.assigned_clinician_id ?? null,
            clinic_physician_id: draft.clinic_physician_id,
            notes: draft.notes.trim(),
            status: communication.status,
          },
        });
        if (!res.data) {
          throw new Error('Communication update returned no data.');
        }
        setCommunication(res.data);
        onUpdated?.();
      } catch (err) {
        console.error('Error saving communication:', err);
        showError('Save Failed', 'Could not save changes.');
        throw err;
      }
    },
    [communication, communicationId, onUpdated, showError]
  );

  const {
    value: editDraft,
    setValue: setEditDraft,
    saveStatus,
    saveNow,
    changeToken,
  } = useAutoSave({
    initialValue: savedDraft,
    onSave: handleAutoSave,
  });

  const editDate = useMemo(
    () =>
      editDraft.communication_date
        ? parseISOToLocalDate(editDraft.communication_date)
        : undefined,
    [editDraft.communication_date]
  );

  const updateEditDraft = React.useCallback(
    (patch: Partial<CommunicationDetailDraft>) => {
      setEditDraft({ ...editDraft, ...patch });
    },
    [editDraft, setEditDraft]
  );

  const isAutosaveReady = Boolean(
    editDraft.communication_date &&
      editDraft.clinic_physician_id &&
      editDraft.notes.trim()
  );

  const doSignAndSubmit = async (credential: ESignatureCredentialInput) => {
    if (!communicationAttestation) return;
    setSigningLoading(true);
    try {
      // Flush pending edits first and bail if they did not persist, so we
      // never sign stale data. onSave already surfaced an error toast.
      const outcome = await saveNow();
      if (outcome !== 'saved') {
        // The save was declined or failed, which shows no toast of its own. The
        // dialog stays open only to preserve a rejected password, and there is
        // no password to preserve here — leaving it open would sit there doing
        // nothing on every further click.
        setSignAttestationOpen(false);
        return;
      }
      const result = await signTask({
        path: { task_id: communicationId },
        body: {
          ...credential,
          attestation_version: communicationAttestation.version,
        },
      });
      if (result.error) {
        console.error('Error signing communication:', result.error);
        showError(
          'Sign Failed',
          getBusinessErrorMessage(result) ?? 'Failed to sign. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
      // Closed only on success, so a rejected e-signature password leaves the
      // dialog and what was typed in place.
      setSignAttestationOpen(false);
    } catch (err) {
      console.error('Error signing communication:', err);
      showError('Sign Failed', 'Failed to sign. Please try again.');
    } finally {
      setSigningLoading(false);
    }
  };

  const handleOpenSignAttestation = async () => {
    // Hold the attestation dialog closed if the pending changes did not save,
    // so the user can fix and retry rather than signing stale data. onSave
    // already surfaced an error toast on a real failure.
    const outcome = await saveNow();
    if (outcome !== 'saved') return;
    setSignAttestationOpen(true);
  };

  const doApprove = async (credential: ESignatureCredentialInput) => {
    if (!communicationAttestation) return;
    setApproving(true);
    try {
      const result = await approveTask({
        path: { task_id: communicationId },
        body: {
          ...credential,
          attestation_version: communicationAttestation.version,
        },
      });
      if (result.error) {
        console.error('Error approving communication:', result.error);
        showError(
          'Approve Failed',
          getBusinessErrorMessage(result) ??
            'Failed to complete. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
      setApproveAttestationOpen(false);
    } catch (err) {
      console.error('Error approving communication:', err);
      showError('Approve Failed', 'Failed to complete. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleBreakSignatures = async () => {
    setBreaking(true);
    try {
      const result = await breakTaskSignatures({
        path: { task_id: communicationId },
      });
      if (result.error) {
        console.error('Error unlocking communication:', result.error);
        showError('Unlock Failed', 'Failed to unlock. Please try again.');
        return;
      }
      await fetchData();
      onUpdated?.();
    } catch (err) {
      console.error('Error unlocking communication:', err);
      showError('Unlock Failed', 'Failed to unlock. Please try again.');
    } finally {
      setBreaking(false);
    }
  };

  const handleExportPdf = async () => {
    if (pdfInFlightRef.current) return;
    pdfInFlightRef.current = true;
    setPdfLoading(true);
    let tab: ReturnType<typeof openPdfTab> = null;
    try {
      // Flush pending edits first; if they did not persist, skip the export so
      // the PDF is never built from stale server data. onSave already toasted.
      const outcome = await saveNow();
      if (outcome !== 'saved') return;
      tab = openPdfTab();
      if (!tab) {
        return;
      }
      const response = await getPatientCommunicationPdf({
        path: { communication_id: communicationId },
      });
      if (!response.data) {
        tab.fail('communication PDF', new Error('no PDF data received'));
        return;
      }
      tab.setBlob(
        new Blob([response.data as BlobPart], { type: 'application/pdf' })
      );
    } catch (err) {
      if (tab) {
        tab.fail('communication PDF', err);
      } else {
        console.error('Error opening communication PDF:', err);
        showError('Print Failed', 'Could not open the print view.');
      }
    } finally {
      setPdfLoading(false);
      pdfInFlightRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (!communication) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-muted-foreground'>Communication not found.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          {onBack && (
            <Button
              variant='outline'
              size='sm'
              onClick={onBack}
              className='flex items-center gap-1.5 flex-shrink-0'
            >
              <ArrowLeft className='h-3.5 w-3.5' />
              Back
            </Button>
          )}
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0 flex-1'>
            <div className='truncate text-sm font-medium text-foreground'>
              {communication.communication_type}
            </div>
            <div className='text-xs text-muted-foreground'>
              Communication date:{' '}
              {formatDateToMMDDYYYY(communication.communication_date)}
            </div>
          </div>
        </div>

        <TaskHeaderControls
          className='w-full sm:w-auto'
          statusItems={
            <TaskWorkflowStatusBadge
              status={communication.status}
              signatures={activeSignatures}
            />
          }
          primaryActions={
            <>
              {canBreakSignatures && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void handleBreakSignatures()}
                  disabled={breaking}
                  className='flex items-center gap-1.5'
                >
                  <Pen className='h-4 w-4' />
                  {breaking ? 'Unlocking...' : 'Unlock & Edit'}
                </Button>
              )}
              {canSign && (
                <Button
                  onClick={() => void handleOpenSignAttestation()}
                  disabled={
                    signingLoading ||
                    attestationsLoading ||
                    !communicationAttestation ||
                    saveStatus === 'saving'
                  }
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {signingLoading ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4' />
                      Sign & Submit
                    </>
                  )}
                </Button>
              )}
              {canApprove && (
                <Button
                  onClick={() => setApproveAttestationOpen(true)}
                  disabled={
                    approving ||
                    attestationsLoading ||
                    !communicationAttestation
                  }
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {approving ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      Approving...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4' />
                      Sign & Complete
                    </>
                  )}
                </Button>
              )}
            </>
          }
          secondaryActions={[
            {
              label: pdfLoading ? 'Opening print view...' : 'Print view',
              icon: pdfLoading ? (
                <div
                  className='animate-spin rounded-full h-3 w-3 mr-2 border-b-2 border-current'
                  aria-hidden='true'
                />
              ) : (
                <ExternalLink className='h-4 w-4 mr-2' aria-hidden='true' />
              ),
              onSelect: () => void handleExportPdf(),
              disabled: pdfLoading || saveStatus === 'saving',
            },
            {
              label: 'View history',
              // Defer opening to the next frame so the Actions dropdown finishes
              // unmounting (and releases its body pointer-events lock) before the
              // dialog mounts — otherwise the page is left unclickable.
              icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => {
                if (historyDialogFrameRef.current !== null) {
                  cancelAnimationFrame(historyDialogFrameRef.current);
                }
                historyDialogFrameRef.current = requestAnimationFrame(() => {
                  historyDialogFrameRef.current = null;
                  setHistoryOpen(true);
                });
              },
            },
          ]}
        />
      </div>

      {/* Sub-header: physician assignment (editable) or signer info (readonly) */}
      <div className='flex items-center gap-4 px-5 py-2 border-b bg-muted/10'>
        {isReadonly ? (
          activeSignatures.length > 0 ? (
            activeSignatures.map(sig => {
              const metadata =
                (sig.signature_metadata as Record<string, unknown> | null) ??
                {};
              const displayName =
                (metadata.signer_display_name as string | undefined) ||
                (metadata.user_name as string | undefined) ||
                (metadata.user_email as string | undefined) ||
                'Unknown signer';
              const credentials = metadata.signer_credentials as
                | string
                | undefined
                | null;
              return (
                <div key={sig.id} className='flex items-center gap-2'>
                  <Label className='text-xs text-muted-foreground whitespace-nowrap'>
                    Signer
                  </Label>
                  <span className='text-sm'>
                    {credentials
                      ? `${displayName}, ${credentials}`
                      : displayName}
                  </span>
                </div>
              );
            })
          ) : null
        ) : (
          <div className='w-48'>
            <DropdownField
              label='Physician'
              htmlFor='comm-detail-physician'
              variant='formSelect'
              sortOptions
              fieldSize='sm'
              placeholder='Select physician'
              options={physicians.map(p => ({
                value: p.id,
                label: formatPhysicianName(p),
              }))}
              value={editDraft.clinic_physician_id}
              onChange={clinic_physician_id =>
                updateEditDraft({ clinic_physician_id })
              }
            />
          </div>
        )}
      </div>

      {/* Save status pill: a zero-height sticky overlay above the scrollable
          body. It floats over the top-right of the form (its own background +
          shadow keep it legible) and stays in view as the form scrolls.
          Zero height means showing/hiding the pill never shifts the layout. */}
      {!isReadonly && (
        <div className='pointer-events-none sticky top-0 z-20 flex h-0 shrink-0 justify-end px-5'>
          <AutoSavePill
            status={isAutosaveReady ? saveStatus : 'idle'}
            debounceMs={1500}
            changeToken={changeToken}
          />
        </div>
      )}

      {/* Body */}
      <div className='flex-1 overflow-auto p-4'>
        {isReadonly ? (
          <Card>
            <CardContent className='p-4 space-y-4'>
              <div className='grid grid-cols-2 gap-4 text-sm'>
                <div>
                  <span className='font-medium text-muted-foreground'>
                    Communication Date
                  </span>
                  <p>
                    {formatDateToMMDDYYYY(communication.communication_date)}
                  </p>
                </div>
                <div>
                  <span className='font-medium text-muted-foreground'>
                    Type
                  </span>
                  <p>{communication.communication_type}</p>
                </div>
              </div>
              {communication.notes && (
                <div>
                  <span className='text-sm font-medium text-muted-foreground'>
                    Communication
                  </span>
                  <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                    {communication.notes}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className='p-4 space-y-4'>
              <div className='space-y-1.5'>
                <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  Date
                </Label>
                <DatePicker
                  date={editDate}
                  onDateChange={nextDate =>
                    updateEditDraft({
                      communication_date: nextDate
                        ? formatDateToISO(nextDate)
                        : null,
                    })
                  }
                  minDate={
                    communication.episode_start_date
                      ? parseISOToLocalDate(communication.episode_start_date)
                      : undefined
                  }
                  maxDate={
                    communication.episode_end_date
                      ? parseISOToLocalDate(communication.episode_end_date)
                      : undefined
                  }
                />
              </div>
              <div className='space-y-1.5'>
                <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  Communication <span className='text-destructive'>*</span>
                </Label>
                <Textarea
                  value={editDraft.notes}
                  onChange={e => updateEditDraft({ notes: e.target.value })}
                  onBlur={() => void saveNow()}
                  placeholder='Describe the communication and any follow-up actions…'
                  className='min-h-[120px] resize-y text-sm'
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <AttestationDialog
        open={signAttestationOpen}
        attestation={communicationAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={signingLoading}
        actionLabel='Sign & Submit'
        onConfirm={credential => void doSignAndSubmit(credential)}
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        attestation={communicationAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={approving}
        actionLabel='Sign & Complete'
        onConfirm={credential => void doApprove(credential)}
        onCancel={() => setApproveAttestationOpen(false)}
      />

      <SignatureHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taskId={communicationId}
      />
    </div>
  );
};
